import type { FlexProps } from '@chakra-ui/react';
import { Box, Flex, Text } from '@chakra-ui/react';
import TransactionAmount from '@ui/components/TransactionAmount';
import { TRANSACTION_TYPES_LP } from '@ui/components/TransactionHistoryDrawer/transactionConstants';
import { getTransactionTypeText } from '@ui/components/TransactionHistoryDrawer/TransactionDetailDrawer';
import { useTranslations } from '@ui/i18n';
import type { TransactionHistoryResponse } from '@ui/queries/history';
import transformTransaction from '@ui/utils/transformTransaction';
import { currentWallet } from '@veno-app/wallet';
import type { FC } from 'react';
import React, { useMemo } from 'react';

const { useAccount } = currentWallet;
type StakingTransactionShortInfoProps = {
  transactionData: TransactionHistoryResponse;
};

const StakingTransactionShortInfo: FC<StakingTransactionShortInfoProps> = ({
  transactionData,
}) => {
  const account = useAccount() ?? '';

  if (!transactionData?.transactions.length || !account) return null;
  const nTransactions = transactionData.transactions.length ?? 0;

  return (
    <Box mt={1} textStyle={{ base: 'caption', desktop: 'bodySmall' }}>
      {nTransactions && (
        <ShortTransactionExplainer
          data={transactionData.transactions[0]}
          account={account}
        />
      )}
      {(nTransactions ?? 0) > 1 && (
        <ShortTransactionExplainer
          mt={1}
          data={transactionData.transactions[1]}
          account={account}
        />
      )}
      {(nTransactions ?? 0) > 2 && nTransactions !== 12 && (
        <Text mt={1} color="text-light">
          +{(nTransactions ?? 0) - 2} transaction
          {nTransactions > 3 ? 's' : ''}
        </Text>
      )}
      {nTransactions === 12 && (
        <Text color="text-light" mt={1}>
          10+ more transactions
        </Text>
      )}
    </Box>
  );
};

const ShortTransactionExplainer = ({
  data,
  account,
  ...flexProps
}: {
  data?: TransactionHistoryResponse['transactions'][0];
  account: string;
} & FlexProps) => {
  const t = useTranslations();
  const transformedTransaction = useMemo(() => {
    const transformFn = transformTransaction(account);
    return data ? transformFn(data) : undefined;
  }, [data, account]);

  return transformedTransaction ? (
    <Flex {...flexProps} flexDir="column">
      <Box>{getTransactionTypeText(t, transformedTransaction)}</Box>
      <Flex>
        {!TRANSACTION_TYPES_LP.includes(transformedTransaction.type) && (
          <TransactionAmount transaction={transformedTransaction} isSmall />
        )}
      </Flex>
    </Flex>
  ) : null;
};

export default StakingTransactionShortInfo;
